import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';
import { ISignedLinkResource } from './models/signed-link.model';

export type DelegatedActivationKind = 'onedrive' | 'sharepoint';

@Injectable({
  providedIn: 'root'
})
export class DelegatedActivationService {

  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = loggingService.getLogger('[DelegatedActivationService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  /**
   * Calls the tenant's delegated cloud-storage activation endpoint on behalf of the
   * currently signed-in user and returns the Microsoft consent URL — the `href` of the
   * {@link ISignedLinkResource} it answers with — that the user must be redirected to.
   *
   * The backend endpoint requires an authenticated user with tenant access; the bearer
   * token is attached by AuthInterceptor. Every caller-supplied part must already be
   * validated: the path segments (providerId, directory, kind) so they are safe to
   * interpolate, and redirectUri via
   * SanitizationService.validateDelegatedRedirectUri() — the backend embeds it in the
   * Microsoft consent URL as a redirect target, so an unchecked value is an open redirect
   * (CWE-601). It is appended via the URL API, which encodes it.
   *
   * @param tenantSelfLink - Validated HATEOAS self link of the current tenant (".../v1/tenants/{tenantId}")
   * @param kind - 'onedrive' | 'sharepoint'
   * @param providerId - Validated identity-provider UUID
   * @param directory - Validated IdP directory (tenant identifier)
   * @param redirectUri - Validated Printix return address for Microsoft to redirect to
   * @returns The validated Microsoft consent URL
   */
  public getDelegatedActivationLink(
    tenantSelfLink: string,
    kind: DelegatedActivationKind,
    providerId: string,
    directory: string,
    redirectUri: string
  ): Observable<string> {
    const methodName = 'getDelegatedActivationLink()';
    this.logger.info(methodName, 'kind: ' + kind);

    const activationUrl = new URL(
      tenantSelfLink + '/identity-providers/' + providerId + '/' + directory +
      '/activate-' + kind + '-delegated'
    );
    activationUrl.searchParams.set('redirect_uri', redirectUri);

    return this.httpClient.get<ISignedLinkResource>(activationUrl.toString()).pipe(
      // Transient failures only. AuthInterceptor already refreshes the token and retries
      // once on 401/403, so retrying everything would put a deterministic answer — a 403
      // "you may not activate delegated consent", or a 404 from a wrong providerId — through
      // four requests and two token refreshes before the page falls back, with the user
      // seeing nothing either way because this flow is silent.
      // of(0) rather than timer(0): the notifier decides *when* to resubscribe, and a timer
      // would defer the second attempt to a later macrotask for no gain — this retry is
      // meant to be immediate, as the plain retry(1) it replaces was.
      retry({ count: 1, delay: (error: HttpErrorResponse | Error) => {
        if (this.isWorthRetrying(error)) {
          return of(0);
        }
        throw error;
      } }),
      map((res: ISignedLinkResource) => {
        const href = this.sanitizationService.validateExternalConsentUrl(res?.href);
        if (!href) {
          this.logger.error(methodName, 'SignedLinkResource href missing or untrusted');
          throw new Error('Invalid activation link');
        }
        return href;
      }),
      // Deliberately NOT routed through ErrorService.handleHttpClientResponseError: this
      // is a silent landing flow (see ActivateDelegatedPage) that must never surface an
      // error modal. 401/403 token refresh is still handled upstream by AuthInterceptor.
      //
      // Typed as a union because the map() above throws here too, so this is not only ever
      // an HttpErrorResponse — a plain Error has no status to report.
      catchError((error: HttpErrorResponse | Error) => {
        const status = error instanceof HttpErrorResponse ? error.status : 'unknown';
        this.logger.error(methodName + ' error - status: ' + status);
        return throwError(() => error);
      })
    );
  }

  // ############### INTERNAL ###############

  /**
   * Whether a second attempt could plausibly succeed.
   *
   * Only network failures (status 0) and server errors are worth repeating. A 4xx here is
   * the backend's considered answer — no access, unknown provider — and asking again just
   * spends the user's time.
   *
   * @param error - The failure from the activation request
   * @returns True when the failure looks transient
   */
  private isWorthRetrying(error: HttpErrorResponse | Error): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    return error.status === 0 || error.status >= 500;
  }
}
